import { routeName } from '~/@common/constants'

import { INTEGRATION_MESSAGE_TYPE } from './constants'
import { getDomainPair } from './integrationDomain'
import { INTEGRATION_IFRAME_MESSAGE } from './messageProtocol'

// ★ 명시 로그아웃 시 반대 origin (sender ↔ receiver) 의 storage 도 정리.
//   인라인이 LOGOUT_CLEAR_ACK 을 보내주면 즉시 finalize (~수백ms).
//   ack 안 오면 (반대 origin 베이직 등 미배포) POST_MESSAGE_FINALIZE_MS timeout 후 finalize.
//
//   상태 흐름 (3 단계):
//     ① 시작 → invisible iframe 띄움 + LOAD_TIMEOUT_MS 안전망
//     ② iframe.load 완료 → LOGOUT_CLEAR 송신 + ack 대기 + POST_MESSAGE_FINALIZE_MS 안전망
//     ③ ack 수신 OR 어느 timeout 든 firing → finalize (모든 cleanup 연산 idempotent)
const POST_MESSAGE_FINALIZE_MS = 1500
const LOAD_TIMEOUT_MS = 3000

const createHiddenIframe = (title: string): HTMLIFrameElement => {
  const iframe = document.createElement('iframe')
  iframe.title = title
  iframe.setAttribute('aria-hidden', 'true')
  iframe.setAttribute('tabindex', '-1')
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none;'
  return iframe
}

export const crossOriginLogoutClear = (): Promise<void> =>
  new Promise((resolve) => {
    const pair = getDomainPair()
    if (!pair) {
      // 통합 흐름 미동작 환경 (라이브 외 + override 없음 매칭 실패 등) → 즉시 resolve, iframe 도 안 만듦.
      resolve()
      return
    }

    const oppositeOrigin = pair.iframeOrigin
    const iframe = createHiddenIframe('integration-logout-clear')

    const finalize = () => {
      window.removeEventListener('message', handleAck)
      clearTimeout(loadTimeoutId)
      clearTimeout(postMessageTimeoutId)
      iframe.remove()
      resolve()
    }

    // ★ 반대 origin 인라인이 LOGOUT_CLEAR 처리 완료 후 보내는 ACK 수신.
    //   ack 받으면 timeout 안 기다리고 즉시 finalize → 반대 origin 통합 배포 케이스에서 ~수백ms.
    const handleAck = (event: MessageEvent) => {
      if (event.origin !== oppositeOrigin) return
      if (typeof event.data !== 'object' || event.data === null) return
      if (event.data.type !== INTEGRATION_MESSAGE_TYPE.LOGOUT_CLEAR_ACK) return
      finalize()
    }
    window.addEventListener('message', handleAck)

    // ① iframe.load 자체가 안 일어나는 케이스 (미배포 / 차단) — 3s 안전망.
    const loadTimeoutId = setTimeout(finalize, LOAD_TIMEOUT_MS)
    let postMessageTimeoutId: ReturnType<typeof setTimeout> | undefined

    // ② iframe.load 완료 = 인라인 listener 등록 완료 → LOGOUT_CLEAR 송신 + ack 안 오면 1.5s 후 finalize.
    iframe.addEventListener('load', () => {
      iframe.contentWindow?.postMessage(INTEGRATION_IFRAME_MESSAGE.LOGOUT_CLEAR, oppositeOrigin)
      postMessageTimeoutId = setTimeout(finalize, POST_MESSAGE_FINALIZE_MS)
    })

    // ?from=<자기 origin> — 인라인이 origin 매칭 검증에 사용.
    iframe.src = `${oppositeOrigin}${routeName.integration.logoutClear}?from=${encodeURIComponent(window.location.origin)}`
    document.body.appendChild(iframe)
  })
